import axios from '@main/axios';
import {
  EMAIL_REGEX,
  NUMBER_REGEX,
  NAME_REGEX,
  ERROR_DEFAULT,
  MAX_PHONE_LENGTH,
  NUM_REGEX,
  PASSWORD_MIN_LENGHT,
  PASSWORD_REGEX,
  DATE_REGEX,
  TIMER_INTERVAL,
} from '@main/helpers/consts';

/** Компонент для работы с формой (отправка, валидация). */
export class Form {
  result;

  /**
   * Создает компонент формы.
   *
   * @param {HTMLFormElement} form Элемент формы.
   */
  constructor(form) {
    this.el = form;
    this.id = this.el.getAttribute('id');
    this.url = this.el.dataset.url;
    this.findElements();
    this.bindEventListeners();
    this.checkSubmitBtn();
  }

  /**
   * Поиск элементов компонента.
   *
   * @returns {void}
   */
  findElements() {
    this.getFields();
    this.submitBtn = this.el.querySelector("button[type='submit']")
      ? this.el.querySelector("button[type='submit']")
      : document.querySelector(`button[form="${this.id}"`);
    this.defaultSubmitBtnText = this.submitBtn.dataset.defaultText;
  }

  /**
   * Привязка обработчиков событий.
   *
   * @returns {void}
   */
  bindEventListeners() {
    this.el.addEventListener('submit', (e) => e.preventDefault());

    this.el.addEventListener('reinitInputValidation', () => {
      this.reinitInputValidation();
    });

    this.bindInputListeners();
    this.updateFields();
  }

  /**
   * Переинициализация полей, событий валидации и ввода.
   *
   * @returns {void}
   */
  reinitInputValidation() {
    this.findElements();
    this.bindInputListeners();
    this.updateFields();
    this.checkSubmitBtn();
  }

  /**
   * Привязка обработчиков событий ввода в поля.
   *
   * @returns {void}
   */
  bindInputListeners() {
    this.fields.forEach((field) => {
      field.input.addEventListener('input', () => {
        this.hideFieldError(field.node);
        this.preventEnteringNumbersInFieldName(field.input);
        this.disallowSpaceInEmailField(field);
        this.checkSubmitBtn();
      });
      field.input.addEventListener('paste', () => {
        this.hideFieldError(field.node);
        this.preventEnteringNumbersInFieldName(field.input);
        this.disallowSpaceInEmailField(field);
        this.checkSubmitBtn();
      });
      field.input.addEventListener('blur', () => {
        this.checkInput(field);
        this.checkSubmitBtn();
      });
    });
  }

  /**
   * Обрабатывает submit формы и показывает нужный modal.
   *
   * @param {Event} e Событие, происходящее при отправке формы.
   * @returns {void}
   */
  submitForm = async (e) => {
    e.preventDefault();
    if (!this.isValid) {
      return;
    }
    try {
      const response = await Form.fetchForm(this.el, this.url);
      this.el.classList.remove('waiting');

      if (response?.success) {
        if (response.data?.redirectUrl) {
          this.el.querySelector("button[type='submit']").disabled = true;
          if (this.el.dataset.noTimeoutRedirect) {
            window.location.href = response.data.redirectUrl;
          } else {
            this.showSuccessMessage(response);
          }
        } else {
          this.showSuccessMessage(response);
          this.clearForm();
        }
      } else {
        this.showErrorMessage(response);
      }
    } catch (e) {
      console.log(e);
      this.showErrorMessage(e.response);
      this.el.classList.remove('waiting');
    }
  };

  showErrorMessage(response) {
    this.submitBtn.classList.add('button--error');
    this.submitBtn.blur();
    if (response) {
      if (response?.message) {
        this.submitBtn.innerHTML = response.message;
      } else {
        this.submitBtn.innerHTML = ERROR_DEFAULT;
      }
    } else {
        this.submitBtn.innerHTML = ERROR_DEFAULT;
    }

    setTimeout(() => {
      this.submitBtn.classList.remove('button--error');
      this.submitBtn.textContent = this.defaultSubmitBtnText;
    }, TIMER_INTERVAL);
  }

  showSuccessMessage() {
    const successMessage = `<svg width="24" height="24">
            <use href="#check"></use>
          </svg> Данные успешно отправлены`;
    this.submitBtn.classList.add('button--success');
    this.submitBtn.blur();
    this.submitBtn.innerHTML = successMessage;

    setTimeout(() => {
      this.submitBtn.classList.remove('button--success');
      this.submitBtn.textContent = this.defaultSubmitBtnText;
    }, TIMER_INTERVAL);
  }

  /**
   * Очистить поля формы.
   *
   * @returns {void}
   */
  clearForm() {
    this.fields.forEach((field) => {
      if (field.input.tagName === 'SELECT' && field.input.tomselect) {
        // Tom Select
        field.input.tomselect.clear();
      } else if (field.input.type === 'checkbox' || field.input.type === 'radio') {
        field.input.checked = false;
      } else {
        field.input.value = '';
      }

      field.node.classList.remove('filled');

      if (field.fileField) {
        field.fileField.innerHTML = '';
      }

      let file = field.node.querySelector('.js-form-field-file-caption');
      if (file) {
        file.innerHTML = '';
      }
    });
    this.checkSubmitBtn();
  }

  /**
   * Дисэблить кнопку, если поля не заполнены.
   *
   * @returns {void}
   */
  checkSubmitBtn() {
    if (!this.submitBtn) {
      return;
    }
    this.submitBtn.disabled = !this.isValidSilent;
  }

  /**
   * Скрыть ошибку у поля.
   *
   * @param {HTMLDivElement} field Контейнер поля ввода.
   * @returns {void}
   */
  hideFieldError(field) {
    field.classList.remove('error');
  }

  /**
   * Запрет ввода чисел в поле имени.
   *
   * @param {HTMLDivElement} field Контейнер поля ввода.
   * @returns {void}
   */
  preventEnteringNumbersInFieldName(field) {
    if (field.classList.contains('js-form-field-input-name')) {
      field.value = field.value.replace(/[+$№^*)@:, .'`";_=<>%#~&!\\(?{}[\]/|\d]/g, '');
    }
  }

  /**
   * Запрет ввода пробела в поле email.
   *
   * @param {HTMLDivElement} field Контейнер поля ввода.
   * @returns {void}
   */
  disallowSpaceInEmailField(field) {
    if (field.input.getAttribute('type') === 'email') {
      field.input.value = field.input.value.replace(' ', '');
    }
  }

  /**
   * Определяет валидность ввода в форме.
   *
   * @returns {boolean} Флаг валидности введенной формы.
   */
  get isValid() {
    let isValid = true;
    this.fields.forEach((field) => {
      if (field.node.classList.contains('error')) {
        field.node.classList.remove('error');
      }
      if (!this.validateField(field.input)) {
        isValid = false;
        field.node.classList.add('error');
      }
    });

    return isValid;
  }

  /**
   * Определяет валидность ввода в форме, не показывая ошибки.
   *
   * @returns {boolean} Флаг валидности введенной формы.
   */
  get isValidSilent() {
    let isValid = true;
    this.fields.forEach((field) => {
      if (!this.validateField(field.input)) {
        isValid = false;
      }
    });

    return isValid;
  }

  /**
   * Определяет валидность ввода в форме.
   *
   * @param {object} field Объект поля ввода.
   * @returns {void}
   */
  checkInput(field) {
    // если поле пустое - показываем ошибку при blur-e если оно required
    if (!field.input.value && field.input.required) {
      field.node.classList.add('error');
    } else {
      if (!this.validateField(field.input)) {
        field.node.classList.add('error');
      } else {
        field.node.classList.remove('error');
      }
    }
  }

  /**
   * Проверка заполнения обязательного поля ввода.
   *
   * @param {HTMLInputElement} input Инпут компонента формы.
   * @returns {boolean} Флаг заполнения поля.
   */
  isFilled(input) {
    if (input.required && !input.value) {
      return false;
    }
    return true;
  }

  /**
   * Проверка валидации поля ввода.
   *
   * @param {HTMLInputElement} input Инпут компонента формы.
   * @returns {boolean} Флаг валидности заполнения поля.
   */
  validateField(input) {
    // Сначала проверяем обязательность поля, в нем наличие паттерна и соответствие ему введенного значения.
    // В случае отсутствия паттерна у обязательного поля - заполнено ли оно.
    // Если поле необязательно - this.result = true
    // после чего производим другие валидации и приводим к флагу this.result
    if (input.required) {
      if (input.pattern || input.dataset.pattern) {
        this.result = Form.isValidPattern(input) && Form.isValidLength(input);
      } else {
        // возвращает true в случае, если input не пустой и далее переходит к проверкам по типу input-а
        this.result = !!input.value;
      }
    } else if (!input.required) {
      this.result = true;
    }

    if (input.disabled) {
      this.result = true;
    }

    switch (input.type) {
      case 'tel':
        this.result = this.result && Form.isValidPhone(input);
        break;

      case 'email':
        this.result = this.result && Form.isValidMail(input);
        break;

      case 'number':
        this.result = this.result && Form.isValidNumber(input);
        break;

      case 'password':
        if (input.classList.contains('js-novalidate')) {
          this.result = true;
          break;
        }
        this.result = this.result && Form.isValidPasswordByLetter(input) && Form.isValidPasswords(input);
        break;

      case 'checkbox':
        this.result = Form.isValidCheckbox(input);
        break;

      case 'hidden':
        this.result = true;
        break;

      case 'date':
        this.result = this.result && Form.isValidDate(input);
        break;
    }

    if (this.result && input.classList.contains('js-form-field-input-name')) {
      this.result = this.result && Form.isValidName(input);
    }

    if (this.result && input.classList.contains('js-form-field-input-number')) {
      this.result = this.result && Form.isValidNumber(input);
    }

    if (this.result && input.classList.contains('js-email-input')) {
      this.result = this.result && Form.isValidMail(input);
    }

    if (this.result && input.classList.contains('js-phone-input')) {
      this.result = this.result && Form.isValidPhone(input);
    }

    if (input.dataset.type === 'password') {
      this.result = this.result && Form.isValidPasswordByLetter(input) && Form.isValidPasswords(input);
    }

    if (this.result && input.classList.contains('js-date-input')) {
      this.result = this.result && Form.isValidDate(input);
    }

    this.result = this.result && Form.checkMinMaxLen(input);
    return this.result;
  }

  /**
   * Отправка формы.
   *
   * @param {HTMLFormElement} formNode DOM узел формы.
   * @param {string} url Url отправки формы.
   * @param {number} id Идентификатор.
   * @returns {object} Ответ сервера в формате, согласному std.
   */
  static async fetchForm(formNode, url, id = undefined) {
    const data = new FormData(formNode);
    const {data: response} = await axios.post(url, data, {
      params: {
        id: id,
      },
    });
    return response;
  }

  /**
   * Проверка валидности элемента по его паттерну.
   *
   * @param {HTMLInputElement} input Инпут с паттерном.
   * @returns {boolean} Валидность элемента по паттерну.
   */
  static isValidPattern(input) {
    if (!input.required && !input.value) {
      return true;
    }
    const patternData = input.pattern || input.dataset.pattern;
    const regex = new RegExp(patternData);
    return regex.test(input.value);
  }

  /**
   * Проверка валидности номера телефона.
   *
   * @param {HTMLInputElement} phoneInput Инпут телефона.
   * @returns {boolean} Ввод телефона валидный.
   */
  static isValidPhone(phoneInput) {
    if (!phoneInput.required && !phoneInput.value) {
      return true;
    }
    return phoneInput.value.length === MAX_PHONE_LENGTH;
  }

  /**
   * Проверка валидности электронной почты.
   *
   * @param {HTMLInputElement} mailInput Инпут почты.
   * @returns {boolean} Валидность ввода почты.
   */
  static isValidMail(mailInput) {
    if (!mailInput.required && !mailInput.value) {
      return true;
    }
    return EMAIL_REGEX.test(mailInput.value);
  }

  /**
   * Проверка валидности поля с датой.
   *
   * @param {HTMLInputElement} dateInput Инпут с датой.
   * @returns {boolean} Валидность ввода даты.
   */
  static isValidDate(dateInput) {
    if (!dateInput.required && !dateInput.value) {
      return true;
    }

    const value = dateInput.value.trim();

    // Проверка формата
    if (!DATE_REGEX.test(value)) {
      return false;
    }

    // Проверка реальности даты
    const parts = value.split('.');
    const day = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const year = parseInt(parts[2], 10);

    const date = new Date(year, month - 1, day);
    return date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  }

  /**
   * Проверка валидности checkbox.
   *
   * @param {HTMLInputElement} checkboxInput Инпут почты.
   * @returns {boolean} Валидность checkbox.
   */
  static isValidCheckbox(checkboxInput) {
    return checkboxInput.checked;
  }

  /**
   * Проверка валидности поля с числом.
   *
   * @param {HTMLInputElement} numberInput Инпут с номером.
   * @returns {boolean} Валидность ввода почты.
   */
  static isValidNumber(numberInput) {
    if (!numberInput.required && !numberInput.value) {
      return true;
    }
    if (numberInput.min && numberInput.max) {
      const min = +numberInput.min;
      const max = +numberInput.max;
      if (+numberInput.value < min || +numberInput.value > max) return;
    }
    return NUMBER_REGEX.test(numberInput.value);
  }

  /**
   * Проверка длины значения.
   *
   * @param {HTMLInputElement} input Инпут.
   * @returns {boolean} Валидность ввода.
   */
  static checkMinMaxLen(input) {
    if (!input.required && !input.value) {
      return true;
    }
    if (input.maxLength > 0 && input.maxLength < input.value.length) {
      return false;
    }
    if (input.minLength > 0 && input.minLength > input.value.length) {
      return false;
    }
    return true;
  }

  /**
   * Проверка валидности поля с ИНН.
   *
   * @param {HTMLInputElement} numberInput Инпут с ИНН.
   * @returns {boolean} Валидность ввода.
   */
  static isValidInn(numberInput) {
    if (!numberInput.required && !numberInput.value) {
      return true;
    }
    if (numberInput.maxLength && numberInput.minLength) {
      if (numberInput.value.length !== numberInput.maxLength && numberInput.value.length !== numberInput.minLength)
        return;
    }
    return NUMBER_REGEX.test(numberInput.value);
  }

  /**
   * Проверка валидности поля с именем.
   *
   * @param {HTMLInputElement} nameInput Инпут с именем.
   * @returns {boolean} Валидность ввода.
   */
  static isValidName(nameInput) {
    if (!nameInput.required && !nameInput.value) {
      return true;
    }
    nameInput.value = nameInput.value.trim();
    return NAME_REGEX.test(nameInput.value);
  }

  /**
   * Проверка валидности длины строки.
   *
   * @param {HTMLInputElement} input Инпут.
   * @returns {boolean} Валидность длины строки.
   */
  static isValidLength(input) {
    if (!input.required && !input.value) {
      return true;
    }
    if (input.maxLength && input.maxLength > 0) {
      if (input.value.length > input.maxLength) {
        return false;
      }
    }

    if (input.minLength && input.minLength > 0) {
      if (input.value.length < input.minLength) {
        return false;
      }
    }
    return true;
  }

  /**
   * Проверка введенных паролей на идентичность.
   *
   * @param {HTMLInputElement} passwordInput Инпут пароля.
   * @returns {boolean} Валидность введенных паролей.
   */
  static isValidPasswords(passwordInput) {
    if (!passwordInput.required && !passwordInput.value) {
      return true;
    }
    const passwordGroup = passwordInput.closest('.js-password-group');

    if (!passwordGroup) {
      return !!passwordInput.value;
    } else {
      const inputs = passwordGroup.querySelectorAll('input');
      // сравниваем значения инпутов только если оба сравниваемых поля заполнены
      if (inputs[0].value && inputs[1].value) {
        const passwordGroupError = passwordInput.closest('.js-password-group').querySelector('.js-caption-error');
        const fields = passwordGroup.querySelectorAll('.js-form-field');
        if (!(inputs[0].value === inputs[1].value) && passwordGroupError) {
          fields.forEach((field) => {
            field.classList.add('error');
            field.querySelector('.js-validation-error').innerHTML = '';
            fields[0].classList.add('hidden-error-icon');
          });
          passwordGroupError.innerHTML = 'Пароли не совпадают';
        } else {
          passwordGroupError.innerHTML = 'Пароли не совпадают';
          fields.forEach((field) => {
            field.classList.remove('error');
            fields[0].classList.remove('hidden-error-icon');
          });
        }
        return inputs[0].value === inputs[1].value;
      } else {
        return true;
      }
    }
  }

  /**
   * Проверка введенного пароля на наличие хотя бы одной буквы & Проверка наличия в пароле заглавных символов.
   *
   * @param {HTMLInputElement} passwordInput Инпут пароля.
   * @returns {boolean} Валидность введенного пароля по наличию буквы.
   */

  static isValidPasswordByLetter(passwordInput) {
    if (!passwordInput.required && !passwordInput.value) {
      return true;
    }
    if (!passwordInput.classList.contains('js-current-password')) {
      const password = passwordInput.value;
      const errorField = passwordInput.closest('.js-form-field').querySelector('.js-validation-error');
      const result =
        NUM_REGEX.test(password) &&
        PASSWORD_REGEX.test(password) &&
        !(password === password.toLowerCase()) &&
        password.length >= PASSWORD_MIN_LENGHT;

      if (result) {
        errorField.innerHTML = '';
      } else {
        errorField.innerHTML = 'Пароль должен содержать не менее 8 знаков и иметь заглавную букву и цифру';
      }
      return result;
    } else {
      return true;
    }
  }

  /**
   * Извлекает все поля формы и сопоставляет их с массивом объектов, содержащим узел поля, элемент ввода и заголовок поля файла.
   *
   * @return {void}
   */
  getFields() {
    const currentFields = this.el.querySelectorAll('.js-form-field');
    const inputs = document.querySelectorAll('input[form=' + this.id + ']');
    const otherFields = [];
    inputs.forEach((input) => {
      const field = input.closest('.js-form-field');
      if (field) {
        otherFields.push(field);
      }
    });
    const allFields = otherFields.length > 0 ? [...currentFields, ...otherFields] : [...currentFields];
    this.fields = allFields.map((field) => ({
      node: field,
      input: field.querySelector('.js-form-field-input'),
      fileField: field.querySelector('.js-field-file-caption'),
    }));
  }

  /**
   * Обновляет список полей формы.
   *
   * @return {void}
   */
  updateFields() {
    document.addEventListener('click', (e) => {
      if (e.target.classList.contains('js-tabs-item') || e.target.closest('.js-tabs-item')) {
        if (this.el.getAttribute('id') === 'user-form') {
          this.getFields();
          this.checkSubmitBtn();
          this.bindInputListeners();
        }
      }
    });
  }
}

document.addEventListener('initFormValidation', (e) => {
  if (e.detail) {
    const element = document.querySelector(e.detail);
    if (element) {
      const form = new Form(element);
      return form.isValid;
    }
  }
});

document.addEventListener('initForm', (e) => {
  if (e.detail) {
    const element = document.querySelector(e.detail);
    if (element) {
      new Form(element);
    }
  }
});
